<script module lang="ts">
  export type ThresholdRange = [lower: number, upper: number]
  export type ThresholdRanges = { good: ThresholdRange; perfect: ThresholdRange }

  /** Checks if value is in the given range */
  const isInRange = (value: number, min: number, max: number) => value >= min && value <= max

  // [x_min, x_max] -> [y_min, y_max] mapping
  // y_min + (value - x_min) / (x_max - x_min) * (y_max - y_min)
  const mapRange = (value: number, [xMin, xMax]: ThresholdRange, [yMin, yMax]: ThresholdRange) =>
    yMin + ((value - xMin) / (xMax - xMin)) * (yMax - yMin)
</script>

<script lang="ts">
  import type { HTMLAttributes } from "svelte/elements"
  import { tweened } from "svelte/motion"
  import { type TransitionConfig } from "svelte/transition"
  import ArrowIcon from "./arrow.svg?icon"
  import PersonIcon from "./person.svg?icon"

  let {
    value = 0,
    thresholds,
    transition = () => ({}),
    ...props
  }: {
    value?: number
    thresholds: ThresholdRanges
    transition?: (node: Element) => TransitionConfig
  } & HTMLAttributes<HTMLDivElement> = $props()

  const azimuth = tweened(value, { duration: 150 })

  $effect(() => {
    $azimuth = mapRange(value, [-1, 1], [-90, 90])
  })
  let isInOptimum = $derived(isInRange(value, ...thresholds.good))
</script>

<div
  {...props}
  class:illumination-compass-back={/* anchor for external customization */ true}
  style:--azimuth={`${$azimuth}deg`}
  transition:transition
>
  <div class="dial">
    <div class="notches"></div>
    <div class="azimuth">
      <div class:optimum={isInOptimum} class="sector"></div>
      <div class="index">
        <ArrowIcon class="icon-arrow" />
      </div>
    </div>
  </div>
  <PersonIcon class="icon-person" />
</div>

<style lang="postcss">
  .illumination-compass-back {
    @apply relative flex aspect-[334/140] w-full max-w-[20.875rem] items-center justify-center;

    perspective: 16.5cm;

    --viewing-angle: /* rad2deg(acos(140 / 334)) */ 63.218deg;
  }

  :global([data-ui-scale="xs"]) .illumination-compass-back {
    @apply max-w-[15.125rem];
  }

  .dial {
    @apply absolute top-0 flex aspect-square w-[78%] justify-center rounded-full;

    transform-origin: top;
    transform: rotateX(var(--viewing-angle));
    transform-style: preserve-3d;
  }

  .notches {
    @apply absolute inset-0 rounded-[inherit];

    mask-image: radial-gradient(
      59.41% 84.09% at 50% 90%,
      rgba(0, 0, 0, 1) 48.54%,
      rgba(0, 0, 0, 0)
    );

    &::before {
      @apply pointer-events-none absolute inset-0 rounded-[inherit] content-[""];

      background-color: rgba(255, 255, 255, 0.4);
      mask-image: url("../IlluminationCompass/notches.svg");
      transform: rotate(var(--azimuth));
    }
  }

  .azimuth {
    @apply absolute inset-0 flex justify-center rounded-[inherit];

    transform-style: preserve-3d;
    transform: rotate(calc(var(--azimuth) + 180deg));
  }

  .sector {
    @apply absolute inset-0 flex justify-center rounded-[inherit];

    transform: rotate(calc(-1 * var(--azimuth)));
    mask-image: conic-gradient(
        from var(--azimuth) at center 50%,
        black calc(-1 * var(--azimuth)),
        transparent 0,
        transparent
      ),
      conic-gradient(from 0 at center 50%, black var(--azimuth), transparent 0, transparent);
    mask-composite: add;

    &::before {
      @apply pointer-events-none absolute inset-0 rounded-[inherit] bg-white/60 transition-[background-color] duration-300 content-[""];

      transform: rotate(var(--azimuth));

      mask-image: radial-gradient(circle at center, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1));
    }

    &.optimum::before {
      @apply bg-[#5DEB50];
    }

    &::after {
      @apply pointer-events-none absolute inset-0 rounded-[inherit] content-[""];

      transform: rotate(var(--azimuth));

      background-image: url("../IlluminationCompass/notches.svg");
    }
  }

  .index {
    @apply absolute h-[1.245rem] w-[1.245rem] self-start rounded-full bg-white;
    box-shadow:
      0px 0px 24px -2px #fff,
      0px 0px 6px -1px #fff;
    transform: translateZ(0.76rem) translateY(-0.55rem) rotate(calc(-1 * var(--azimuth) + 180deg))
      rotateX(calc(-1 * var(--viewing-angle)));
  }
  .index > :global(:first-child) {
    @apply absolute h-[0.308rem] w-[0.618rem];
    top: -9px;
    left: 50%;
    filter: drop-shadow(white 0 0 2px);
    transform-origin: center 18px;
    transform: translate(-50%) rotateZ(calc(sin(var(--azimuth)) * 2deg))
      rotateX(calc(sin(var(--azimuth)) * -60deg)) rotate(calc(var(--azimuth)));
  }

  .illumination-compass-back > :global(:last-child) {
    @apply absolute top-[41.42%] h-[3rem] w-[3rem] -translate-y-1/2 text-[#B5FF6A];
  }

  :global([data-ui-scale="xs"]) .illumination-compass-back > :global(:last-child) {
    @apply top-[39%];
  }
</style>
